import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary — catches JS errors in child components and shows a fallback UI
 * instead of crashing the entire dashboard.
 *
 * Usage:
 *   <ErrorBoundary name="DraftSpace">
 *     <DraftSpace ... />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[ErrorBoundary:${this.props.name || 'unknown'}]`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 rounded-xl bg-slate-800/50 border border-slate-700 min-h-[200px]">
          <AlertTriangle className="w-10 h-10 text-gold-bright mb-3" />
          <h3 className="text-slate-200 font-semibold text-lg mb-1">
            Something went wrong
          </h3>
          <p className="text-slate-400 text-sm mb-4 text-center max-w-md">
            {this.props.name
              ? `The ${this.props.name} section encountered an error.`
              : 'This section encountered an error.'}
            {' '}Your work is safe — try refreshing this panel.
          </p>
          <button
            onClick={this.handleRetry}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-flow-blue hover:bg-flow-blue-hover text-white text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
